import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../api.js';

function StatCard({ label, value, accent = false }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent ? 'text-cyan-400' : 'text-zinc-100'}`}>{value}</div>
    </div>
  );
}

// Vertical bar chart — one bar per day; height ∝ page views, with uniques
// overlaid solid at the base. No chart lib (bundle-size rule).
function TrafficChart({ series, lang }) {
  const max = Math.max(1, ...series.map((d) => d.pageViews));
  const fmtDay = (day) =>
    new Date(day + 'T12:00:00').toLocaleDateString(lang?.startsWith('en') ? 'en-CA' : 'fr-CA', {
      month: 'short', day: 'numeric',
    });
  return (
    <div className="flex items-end gap-[3px] h-40">
      {series.map((d) => (
        <div key={d.day} className="group relative flex-1 flex flex-col justify-end h-full">
          <div className="w-full rounded-t bg-zinc-700/40" style={{ height: `${(d.pageViews / max) * 100}%` }}>
            <div
              className="absolute bottom-0 w-full rounded-t bg-cyan-500/70 group-hover:bg-cyan-400"
              style={{ height: `${(d.uniques / max) * 100}%` }}
            />
          </div>
          <div className="absolute -top-9 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 whitespace-nowrap rounded bg-zinc-800 border border-zinc-700 px-2 py-1 text-[10px] text-zinc-200 shadow-lg">
            {fmtDay(d.day)} · {d.uniques} / {d.pageViews}
          </div>
        </div>
      ))}
    </div>
  );
}

function BreakdownTable({ title, rows, emptyLabel }) {
  return (
    <div className="card p-4 space-y-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      <table className="w-full text-sm">
        <tbody>
          {rows.length === 0 && (
            <tr><td className="py-4 text-center text-zinc-500">{emptyLabel}</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-zinc-800/60 last:border-0">
              <td className="py-1.5 text-zinc-300 truncate max-w-0 w-full"><code className="text-xs">{r.key}</code></td>
              <td className="py-1.5 text-right tabular-nums text-zinc-400 whitespace-nowrap">{r.hits}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TrafficAdmin() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/stats/traffic')
      .then((res) => setData(res.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="px-8 py-6 text-zinc-500 text-sm">{t('common.loading')}</div>;

  return (
    <div className="px-8 py-6 space-y-6">
      <div>
        <h1 className="text-lg font-semibold">{t('traffic.title')}</h1>
        <p className="text-sm text-zinc-500">{t('traffic.subtitle', { days: data.windowDays })}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={t('traffic.live')} value={data.visitors24h} accent />
        <StatCard label={t('traffic.uniquesToday')} value={data.today.uniques} />
        <StatCard label={t('traffic.viewsToday')} value={data.today.pageViews} />
        <StatCard label={t('traffic.viewsWindow', { days: data.windowDays })} value={data.totals.pageViews} />
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">{t('traffic.chartTitle')}</h2>
          <div className="flex items-center gap-3 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-cyan-500/70" />{t('traffic.legendUniques')}</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-zinc-700/60" />{t('traffic.legendViews')}</span>
          </div>
        </div>
        <TrafficChart series={data.series} lang={i18n.language} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <BreakdownTable title={t('traffic.topPages')} rows={data.topPages} emptyLabel={t('traffic.empty')} />
        <BreakdownTable title={t('traffic.topReferrers')} rows={data.topReferrers} emptyLabel={t('traffic.empty')} />
      </div>

      <p className="text-xs text-zinc-600 leading-relaxed max-w-2xl">{t('traffic.privacyNote')}</p>
    </div>
  );
}
